import { Button, Card, Modal } from '@playerbase/ui';

interface PreviewModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  buttonLabel: string;
}

// Approximate preview of the recruitment post the bot will publish into
// each configured channel. Mirrors the Discord-styled posted-message
// preview used by the feedback-form builder (preview-modal.tsx). The
// description supports the small subset of Discord markdown studios use
// most: bullet lines that start with "- " or "* " render as a bulleted
// list, and **bold** / *italic* runs render inline. Anything else is
// preserved as-is with line breaks intact.

export function PreviewModal({
  open,
  onClose,
  title,
  description,
  buttonLabel,
}: PreviewModalProps): JSX.Element {
  const displayTitle = title.trim() === '' ? 'Untitled recruitment post' : title;
  const displayLabel = buttonLabel.trim() === '' ? 'Become a tester' : buttonLabel;
  return (
    <Modal open={open} onClose={onClose} labelledById="recruitment-preview-title" size="lg">
      <Card>
        <Card.Header>
          <Card.Title id="recruitment-preview-title">Preview</Card.Title>
          <Card.Description>
            How the recruitment post will look in Discord. The button is inert here.
          </Card.Description>
        </Card.Header>
        <Card.Content>
          <div className="rounded-lg bg-discord-surface px-5 py-4 text-discord-text">
            <div className="text-base font-semibold text-white">{displayTitle}</div>
            {description.trim() !== '' ? (
              <div className="mt-1 text-sm text-discord-text-muted">
                <DiscordMarkdown source={description} />
              </div>
            ) : null}
            <div className="mt-3">
              <span className="inline-flex cursor-not-allowed items-center gap-2 rounded-[3px] bg-discord-blurple px-3 py-1.5 text-sm font-medium text-white">
                {displayLabel}
              </span>
            </div>
          </div>
        </Card.Content>
        <Card.Footer>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </Card.Footer>
      </Card>
    </Modal>
  );
}

// Groups consecutive bullet lines into <ul>s; everything else falls
// through as a paragraph block. Discord treats lines starting with "- "
// or "* " as list items.
function DiscordMarkdown({ source }: { source: string }): JSX.Element {
  const lines = source.split('\n');
  const blocks: Array<{ kind: 'bullets'; items: string[] } | { kind: 'text'; text: string }> = [];
  let buffer: string[] = [];

  function flushText(): void {
    if (buffer.length === 0) return;
    blocks.push({ kind: 'text', text: buffer.join('\n') });
    buffer = [];
  }

  for (const line of lines) {
    const bulletMatch = /^\s*[-*]\s+(.*)$/.exec(line);
    if (bulletMatch !== null) {
      flushText();
      const last = blocks[blocks.length - 1];
      if (last !== undefined && last.kind === 'bullets') {
        last.items.push(bulletMatch[1] ?? '');
      } else {
        blocks.push({ kind: 'bullets', items: [bulletMatch[1] ?? ''] });
      }
    } else {
      buffer.push(line);
    }
  }
  flushText();

  return (
    <>
      {blocks.map((block, i) =>
        block.kind === 'bullets' ? (
          <ul key={i} className="ml-4 list-disc space-y-0.5">
            {block.items.map((item, j) => (
              <li key={j}>{renderInline(item)}</li>
            ))}
          </ul>
        ) : (
          <p key={i} className="whitespace-pre-wrap">
            {renderInline(block.text)}
          </p>
        ),
      )}
    </>
  );
}

// Inline **bold** and *italic* / _italic_. Tokens nest one level deep
// which covers the realistic recruitment-post wording without dragging
// in a markdown library.
function renderInline(text: string): JSX.Element {
  const parts: Array<string | JSX.Element> = [];
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(
        <strong key={key++} className="font-semibold text-white">
          {token.slice(2, -2)}
        </strong>,
      );
    } else {
      parts.push(<em key={key++}>{token.slice(1, -1)}</em>);
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return <>{parts}</>;
}
